import { useEffect, useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { useCookies } from 'react-cookie';
import { isBrowser } from '../utils/is-browser';
import { sendEvent } from '../utils/analytics';

export const SPLIT_VARIANT = 'split_variant';

export const useSplitTest = (data) => {
  const [cookie, setCookie, removeCookie] = useCookies([SPLIT_VARIANT]);
  const [isB, setIsB] = useState(false);

  // If no "split_variant" cookie is found then assign the user to a bucket
  if (!cookie?.[SPLIT_VARIANT]) {
    const variants = ['a', 'b'];
    const bucket = variants[Math.floor(Math.random() * variants.length)];
    setCookie(SPLIT_VARIANT, bucket);
  }

  // Pulls an existing split test for this page
  const { allContentfulSplitTest } =
    useStaticQuery(graphql`
      query ContentfulSplitTest {
        allContentfulSplitTest {
          nodes {
            a {
              ... on Page {
                contentful_id
              }
            }
            b {
              ...PageAdviceFragment
              ...PageContentFragment
              ...PageEventCategoryFragment
              ...PageEventsLandingFragment
              ...PageLegalFragment
              ...PageLegalHomepageFragment
              ...PageLegalWhatsNewFragment
              ...PagePersonFragment
              ...PagePersonalisedAdviceFragment
              ...PagePolicyFragment
              ...PagePolicyLibraryFragment
              ...PagePressReleaseFragment
              ...PagePressReleaseListingsFragment
              ...PageServiceFragment
              ...PageShopFragment
              ...PageSplashFragment
              ...PageStandardEventFragment
              ...PageUpcomingCoursesFragment
              ...PageUpdatesInsightsAndImpactFragment
              ...PageUpdatesInsightsAndImpactListingsFragment
            }
            testId
          }
        }
      }
    `) || {};

  // Prevents hydration error
  useEffect(() => {
    setIsB(cookie?.[SPLIT_VARIANT] && cookie?.[SPLIT_VARIANT] === 'b');
  }, [data, cookie]);

  // Returns the matching "b" test
  const { page } = data;
  const matchingSplitTest = allContentfulSplitTest?.nodes?.find(
    (splitTest) => splitTest.a?.contentful_id === page.contentful_id
  );

  // Sends a tracking event for this split test
  useEffect(() => {
    if (cookie?.[SPLIT_VARIANT] && matchingSplitTest) {
      sendEvent({
        event: 'triggerSplitTest',
        variant: cookie?.[SPLIT_VARIANT],
        testId: matchingSplitTest.testId,
      });
    }
  }, [cookie, matchingSplitTest]);

  if (!isBrowser) {
    return data;
  }

  // Checks if the user is on the "b" variant
  if (matchingSplitTest) {
    // Sets a cookie for this split test ID
    setCookie('split_test_id', matchingSplitTest?.testId);

    // Returns the "b" variant's page content
    if (isB) {
      return {
        ...data,
        page: {
          ...matchingSplitTest.b,
          navigation: page.navigation,
          contentful_id: page.contentful_id,
          slug: page.slug,
        },
      };
    }
  } else {
    // Removes the split test ID cookie if there was no matching split test
    removeCookie('split_test_id');
  }

  return data;
};
